import React, { useState, useEffect } from 'react';
import { postAPI } from '../../../app/api';
import Swal from 'sweetalert2';

const SMSLog = () => {
    const [smsHistory, setSmsHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedSMS, setExpandedSMS] = useState(null);
    const [smsRecipients, setSmsRecipients] = useState({});
    const [loadingRecipients, setLoadingRecipients] = useState({});

    useEffect(() => {
        fetchSMSHistory();
    }, []);

    const fetchSMSHistory = async () => {
        try {
            setLoading(true);
            const response = await postAPI('sms/history', {});
            if (response?.status === 1) {
                setSmsHistory(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching SMS history:', error);
            Swal.fire('Error', 'Failed to fetch SMS history', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchSMSRecipients = async (smsId) => {
        try {
            setLoadingRecipients(prev => ({ ...prev, [smsId]: true }));
            const response = await postAPI('sms/history/details', { id: smsId });
            if (response?.status === 1) {
                setSmsRecipients(prev => ({ ...prev, [smsId]: response?.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching SMS recipients:', error);
            Swal.fire('Error', 'Failed to fetch recipients', 'error');
        } finally {
            setLoadingRecipients(prev => ({ ...prev, [smsId]: false }));
        }
    };

    const toggleRecipients = async (smsId) => {
        if (expandedSMS === smsId) {
            setExpandedSMS(null);
        } else {
            setExpandedSMS(smsId);
            if (!smsRecipients[smsId]) {
                await fetchSMSRecipients(smsId);
            }
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'completed': { class: 'badge-success', text: 'Completed' },
            'processing': { class: 'badge-warning', text: 'Processing' },
            'failed': { class: 'badge-danger', text: 'Failed' },
            'pending': { class: 'badge-secondary', text: 'Pending' },
            'sent': { class: 'badge-success', text: 'Sent' }
        };
        const config = statusConfig[status] || { class: 'badge-secondary', text: status };
        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading SMS history...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">SMS History</h5>
                    <button className="btn btn-sm btn-primary" onClick={fetchSMSHistory}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                    </button>
                </div>
                {smsHistory.length === 0 ? (
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        No SMS history found. Send your first SMS to see it here.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover cus-table">
                            <thead>
                                <tr>
                                    <th>Date & Time</th>
                                    <th>Group</th>
                                    <th>Message Preview</th>
                                    <th>Recipients</th>
                                    <th>Sent</th>
                                    <th>Failed</th>
                                    <th>Status</th>
                                    <th>Sent By</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {smsHistory.map((sms) => (
                                    <React.Fragment key={sms.id}>
                                        <tr>
                                            <td>{formatDateTime(sms.sent_at)}</td>
                                            <td>{sms.group_name || 'N/A'}</td>
                                            <td>
                                                <div className="text-truncate" style={{ maxWidth: '250px' }} title={sms.message}>
                                                    {sms.message}
                                                </div>
                                            </td>
                                            <td>{sms.recipients_count}</td>
                                            <td>
                                                <span className="badge badge-success">{sms.sent_count || 0}</span>
                                            </td>
                                            <td>
                                                <span className="badge badge-danger">{sms.failed_count || 0}</span>
                                            </td>
                                            <td>{getStatusBadge(sms.status)}</td>
                                            <td>{sms.sent_by || 'N/A'}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => toggleRecipients(sms.id)}
                                                >
                                                    {expandedSMS === sms.id ? (
                                                        <>
                                                            <i className="bi bi-chevron-up"></i> Hide
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-chevron-down"></i> View
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedSMS === sms.id && (
                                            <tr>
                                                <td colSpan="9" className="bg-light">
                                                    <div className="p-3">
                                                        <h6>Full Message:</h6>
                                                        <div className="mb-3 p-2 bg-white border rounded">
                                                            {sms.message}
                                                        </div>
                                                        <div className="mb-3">
                                                            <small className="text-muted">
                                                                Characters: {sms.message?.length || 0} 
                                                            </small>
                                                        </div>
                                                        <h6>Recipients Details:</h6>
                                                        {loadingRecipients[sms.id] ? (
                                                            <div className="text-center py-3">
                                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </div>
                                                        ) : smsRecipients[sms.id]?.length > 0 ? (
                                                            <div className="table-responsive">
                                                                <table className="table table-sm table-bordered cus-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Recipient Name</th>
                                                                            <th>Mobile Number</th>
                                                                            <th>Status</th>
                                                                            <th>Sent At</th>
                                                                            <th>Error Message</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {smsRecipients[sms.id].map((recipient, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>{recipient.recipient_name}</td>
                                                                                <td>{recipient.recipient_mobile}</td>
                                                                                <td>{getStatusBadge(recipient.status)}</td>
                                                                                <td>{formatDateTime(recipient.sent_at)}</td>
                                                                                <td>
                                                                                    {recipient.error_message ? (
                                                                                        <span className="text-danger small">
                                                                                            {recipient.error_message}
                                                                                        </span>
                                                                                    ) : (
                                                                                        '-'
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted">No recipient details available</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SMSLog;
